import { Injectable } from '@angular/core';
import { BasicConstants } from 'src/app/core/constants/basics-constant';
import { ITreeMenu } from 'src/app/core/models/shared/Auth/ITreeMenu.interface';

@Injectable({
  providedIn: 'root',
})
export class RoutePermissionService {
  private routePermissionMap: Record<string, string> | null = null;
  private cachedMenu: ITreeMenu[] | null = null;
  private userPermissions: string[] | null = null;

  buildRouteMapFromMenu(menu: ITreeMenu[]): Record<string, string> {
    if (this.routePermissionMap && this.cachedMenu === menu) {
      return this.routePermissionMap;
    }

    this.cachedMenu = menu;
    this.routePermissionMap = this.extractRoutePermissionMap(menu);
    return this.routePermissionMap;
  }

  getUserPermissions(): string[] {
    if (this.userPermissions) {
      return this.userPermissions;
    }

    this.userPermissions = this.extractPermissionsFromToken();
    return this.userPermissions;
  }

  getRequiredPermission(url: string, menu: ITreeMenu[]): string | null {
    const map = this.buildRouteMapFromMenu(menu);
    const currentUrl = this.normalizeRoute(url).toLowerCase();
    return map[currentUrl] ?? this.findDynamicPermission(currentUrl, map);
  }

  hasPermission(requiredPermission: string): boolean {
    const userPermissions = new Set(this.getUserPermissions());

    if (userPermissions.has(requiredPermission)) {
      return true;
    }

    const baseName = this.getPermissionBaseName(requiredPermission);
    if (!baseName) {
      return false;
    }

    // Menu permissions end with .View; token may grant .Full for the same screen.
    return userPermissions.has(`${baseName}.Full`) || userPermissions.has(`${baseName}.View`);
  }

  clearCache(): void {
    this.routePermissionMap = null;
    this.cachedMenu = null;
    this.userPermissions = null;
  }

  clearUserPermissionsCache(): void {
    this.userPermissions = null;
  }

  private extractRoutePermissionMap(menu: ITreeMenu[]): Record<string, string> {
    const map: Record<string, string> = {};
    const rootNodes = this.unwrapScreenMenu(menu);

    const walk = (items: ITreeMenu[]) => {
      for (const item of items) {
        const route = item.route?.trim();
        const permission = item.permission?.trim();

        if (route && route !== 'NULL' && permission) {
          map[this.normalizeRoute(route).toLowerCase()] = permission;
        }

        if (item.childNode?.length) {
          walk(item.childNode);
        }
      }
    };

    walk(rootNodes);
    return map;
  }

  private unwrapScreenMenu(menu: ITreeMenu[]): ITreeMenu[] {
    if (
      menu.length > 0 &&
      (menu[0].name === 'Screen') &&
      Array.isArray(menu[0].childNode)
    ) {
      return menu[0].childNode;
    }

    return menu;
  }

  private extractPermissionsFromToken(): string[] {
    const token = localStorage.getItem(BasicConstants.USER_TOKEN);

    if (!token) {
      return [];
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const permissions = payload.Permission;

      if (Array.isArray(permissions)) {
        return [
          ...new Set(
            permissions
              .filter((permission: unknown): permission is string => typeof permission === 'string')
              .map((permission: string) => permission.trim())
              .filter(Boolean),
          ),
        ];
      }

      if (typeof permissions === 'string' && permissions.trim()) {
        return [permissions.trim()];
      }
    } catch {
      return [];
    }

    return [];
  }

  private findDynamicPermission(
    currentUrl: string,
    map: Record<string, string>
  ): string | null {
    const segments = currentUrl.split('/').filter(Boolean);
    let path = '';
    let result: string | null = null;

    for (const segment of segments) {
      path += '/' + segment;
      if (map[path]) {
        result = map[path];
      }
    }

    return result;
  }

  private normalizeRoute(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    return path.startsWith('/') ? path : `/${path}`;
  }

  private getPermissionBaseName(permission: string): string | null {
    const lastDotIndex = permission.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return null;
    }

    return permission.substring(0, lastDotIndex);
  }
}
